import React, { useEffect, useState, useCallback } from "react"
import {
  CContainer,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CButton,
  CFormSelect,
  CSpinner,
  CAlert,
} from "@coreui/react"
import { useAuth } from "../hooks/useAuth"
import {
  getUsers,
  approveUser,
  rejectUser,
  changeUserRole,
} from "../api/admin"
import "./UsersManagementPage.css"

const ROLES = ["demo", "premium", "admin"]

const UsersManagementPage = () => {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [busyId, setBusyId] = useState(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      const res = await getUsers()
      setUsers(res.data)
    } catch (err) {
      setError(
        err.response?.data?.detail || "Failed to load users."
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  const withBusy = async (id, fn) => {
    setBusyId(id)
    setError("")
    try {
      await fn()
      await fetchUsers()
    } catch (err) {
      setError(err.message || "Action failed.")
    } finally {
      setBusyId(null)
    }
  }

  const approveUserAction = (id) =>
    withBusy(id, async () => {
      try {
        await approveUser(id)
      } catch (err) {
        throw new Error(
          err.response?.data?.detail || "Could not approve user."
        )
      }
    })

  const rejectUserAction = (id) =>
    withBusy(id, async () => {
      try {
        await rejectUser(id)
      } catch (err) {
        throw new Error(
          err.response?.data?.detail || "Could not reject user."
        )
      }
    })

  const changeRole = (id, role) =>
    withBusy(id, async () => {
      try {
        await changeUserRole(id, role)
      } catch (err) {
        throw new Error(
          err.response?.data?.detail || "Could not change role."
        )
      }
    })

  return (
    <CContainer fluid className="py-4 users-page">
      <h2 className="page-title">Manage Users</h2>

      {error && <CAlert color="danger">{error}</CAlert>}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center users-page-loading">
          <CSpinner color="primary" />
        </div>
      ) : (
        <CTable align="middle" hover responsive>
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell>Username</CTableHeaderCell>
              <CTableHeaderCell>Email</CTableHeaderCell>
              <CTableHeaderCell>Status</CTableHeaderCell>
              <CTableHeaderCell>Role</CTableHeaderCell>
              <CTableHeaderCell>Joined</CTableHeaderCell>
              <CTableHeaderCell>Actions</CTableHeaderCell>
            </CTableRow>
          </CTableHead>

          <CTableBody>
            {users.map((u) => (
              <CTableRow key={u.id}>
                <CTableDataCell>{u.username}</CTableDataCell>
                <CTableDataCell className="text-muted">{u.email}</CTableDataCell>
                <CTableDataCell>
                  <CBadge color={u.is_approved ? "success" : "warning"}>
                    {u.is_approved ? "Approved" : "Pending"}
                  </CBadge>
                </CTableDataCell>
                <CTableDataCell>
                  <CFormSelect
                    size="sm"
                    className="user-role-select"
                    value={u.role}
                    disabled={busyId === u.id || u.id === currentUser?.id}
                    onChange={(e) => changeRole(u.id, e.target.value)}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </CFormSelect>
                </CTableDataCell>
                <CTableDataCell className="text-muted">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </CTableDataCell>
                <CTableDataCell>
                  {!u.is_approved && (
                    <div className="d-flex gap-2">
                      <CButton size="sm" color="success" variant="outline" disabled={busyId === u.id} onClick={() => approveUserAction(u.id)}>
                        Approve
                      </CButton>
                      <CButton size="sm" color="danger" variant="outline" disabled={busyId === u.id} onClick={() => rejectUserAction(u.id)}>
                        Reject
                      </CButton>
                    </div>
                  )}
                </CTableDataCell>
              </CTableRow>
            ))}
          </CTableBody>
        </CTable>
      )}
    </CContainer>
  )
}

export default UsersManagementPage
